import React, { useState, useEffect } from 'react';
import { walletAPI } from '../services/api';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  AccountBalanceWallet,
  ArrowBack,
  FilterList,
  CalendarToday,
  TrendingUp,
  TrendingDown,
  Schedule,
  Payment,
  Diamond,
  AttachMoney,
  Clear,
  DateRange,
  ContentCopy
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNav from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import useResponsive from '../hooks/useResponsive';
import './Wallet.css';

const Wallet = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletGems, setWalletGems] = useState(0);
  const [selectedType, setSelectedType] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedTransactionFilter, setSelectedTransactionFilter] = useState('All');
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [error, setError] = useState(null);

  const transactionTypes = [
    { name: 'All', icon: <FilterList /> },
    { name: 'Booking', icon: <CalendarToday /> },
    { name: 'Reschedule', icon: <Schedule /> },
    { name: 'Earned', icon: <TrendingUp /> },
    { name: 'Refunded', icon: <TrendingDown /> },
  ];

  const transactionStatuses = [
    'All',
    'PENDING',
    'COMPLETED',
    'FAILED',
  ];

  const transactionFilters = [
    'All',
    'Credit',
    'Debit', 
    'Refund',
    'Payment',
    'Withdrawal',
  ];

  useEffect(() => {
    fetchWalletData();
    fetchTransactions();
  }, [selectedType, selectedStatus, selectedDateRange]);

  const fetchWalletData = async () => {
    try {
      const response = await walletAPI.getWalletBalance();
      if (response.success) {
        setWalletBalance(response.data.balance);
        setWalletGems(response.data.gems);
      } else {
        setError('Failed to load wallet data');
      }
    } catch (error) {
      setError('Failed to load wallet data');
      // Fallback to user data
      setWalletBalance(user?.walletBalance || 0);
      setWalletGems(user?.walletGems || 0);
    }
  };

  const fetchTransactions = async () => {
    let params = {};
    try {
      setLoading(true);
      
      // Prepare query parameters
      let typeParam = 'all';
      if (selectedType !== 'All') {
        // Map the UI filter names to backend parameter values
        switch (selectedType) {
          case 'Booking':
            typeParam = 'booking';
            break;
          case 'Reschedule':
            typeParam = 'reschedule';
            break;
          case 'Earned':
            typeParam = 'credit';
            break;
          case 'Refunded':
            typeParam = 'refund';
            break;
          default:
            typeParam = selectedType.toLowerCase();
        }
      }
      
      params = {
        type: typeParam,
        status: selectedStatus !== 'All' ? selectedStatus : 'all',
        page: 1,
        limit: 50
      };

      // Add date range if selected
      if (selectedDateRange) {
        params.startDate = selectedDateRange.start.toISOString();
        params.endDate = selectedDateRange.end.toISOString();
      }


      const response = await walletAPI.getWalletTransactions(params);
      
      if (response.success) {
        setTransactions(response.data.transactions);
      } else {
        setError('Failed to load transactions');
        setTransactions([]);
      }
    } catch (error) {
      setError('Failed to load transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const getAmountColor = (transactionType) => {
    if (!transactionType || typeof transactionType !== 'string') {
      return '#1a1a1a'; // Default color for undefined/invalid types
    }
    
    // Handle lowercase transaction types from database
    if (transactionType === 'debit' || transactionType === 'withdrawal') {
      return '#dc2626'; // Red for debits/withdrawals
    } else if (transactionType === 'credit' || transactionType === 'refund') {
      return '#16a34a'; // Green for credits/refunds
    } else if (transactionType === 'payment') {
      return '#dc2626'; // Red for payments (money going out)
    }
    
    // Fallback for any other types
    return '#1a1a1a'; // Default color
  };


  const formatDate = (date) => {
    if (!date) {
      return 'Date not available';
    }
    
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(dateObj);
    } catch (error) {
      return 'Date formatting error';
    }
  };

  const getTransactionIcon = (transaction) => {
    if (!transaction || !transaction.type) {
      return <TrendingUp sx={{ color: '#16a34a' }} />;
    }
    
    if (transaction.type === 'credit' || transaction.type === 'refund') {
      return <TrendingUp sx={{ color: '#16a34a' }} />;
    } else {
      return <TrendingDown sx={{ color: '#dc2626' }} />;
    }
  };

  const clearFilters = () => {
    setSelectedType('All');
    setSelectedStatus('All');
    setSelectedTransactionFilter('All');
    setSelectedDateRange(null);
  };

  const handleTransactionClick = (transaction) => {
    
    // If transaction has a bookingId, navigate to booking details
    if (transaction.bookingId) {
      navigate(`/bookings`, { 
        state: { 
          showBookingDetails: true, 
          bookingId: transaction.bookingId,
          fromTransaction: true
        } 
      });
    }
    // If transaction has an orderId, navigate to order details
    else if (transaction.orderId) {
      navigate(`/product-order/${transaction.orderId}`);
    }
    // If transaction has a reference that looks like a booking number
    else if (transaction.description && transaction.description.includes('BK-')) {
      // Extract booking number from description
      const bookingMatch = transaction.description.match(/BK-[A-Z0-9]+/);
      if (bookingMatch) {
        navigate(`/bookings`, { 
          state: { 
            showBookingDetails: true, 
            bookingNumber: bookingMatch[0],
            fromTransaction: true
          } 
        });
      }
    }
    // Default: just go to bookings page
    else {
      navigate(`/bookings`);
    }
  };

  if (loading) {
    return (
      <Box className="loading-container">
        <BrandLoader />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: '#0A2A66', 
        color: 'white', 
        p: 2,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: 'white' }}
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                p: 1,
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AccountBalanceWallet sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Wallet
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Your balance and payment history
                </Typography>
              </Box>
            </Box>
          </Box>
          
        </Box>
      </Box>

      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2,
          position: 'relative'
        }}>
        
        
        {/* Wallet Info Card */}
        <Card sx={{ 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #006AF9 0%, #093C86 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              opacity: 0.1,
              zIndex: 0
            }}>
              <AccountBalanceWallet sx={{ fontSize: 120 }} />
            </Box>
            
            <Grid container spacing={3}>
              {/* Gems Section */}
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Diamond sx={{ fontSize: 24, mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Gems
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {walletGems.toFixed(0)}
                </Typography>
              </Grid>

              {/* Balance Section */}
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney sx={{ fontSize: 24, mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Balance
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ${walletBalance.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filter Section */}
        <Box sx={{ mb: 3 }}>
          {/* Transaction Type Filters */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Filter by Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
              {transactionTypes.map((type) => (
                <Box
                  key={type.name}
                  onClick={() => setSelectedType(type.name)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    minWidth: 80,
                    borderRadius: 2,
                    backgroundColor: selectedType === type.name ? '#006AF9' : 'white',
                    color: selectedType === type.name ? 'white' : '#006AF9',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: 1,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    }
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    {React.cloneElement(type.icon, { 
                      sx: { fontSize: 32 },
                      color: selectedType === type.name ? 'white' : 'inherit'
                    })}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center' }}>
                    {type.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Date Range and Filter Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedDateRange && (
                <Chip
                  label={`${selectedDateRange.start.toLocaleDateString()} - ${selectedDateRange.end.toLocaleDateString()}`}
                  onDelete={() => setSelectedDateRange(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setFilterDialogOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Filters
            </Button>
          </Box>
        </Box>

        {/* Transaction History */}
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A1A1A' }}>
                  Transaction History
                </Typography>
                <Typography variant="body2" sx={{ color: '#666666' }}>
                  A list of historical transactions
                </Typography>
              </Box>
              <IconButton
                onClick={() => setFilterDialogOpen(true)}
                sx={{ color: '#666666' }}
              >
                <FilterList />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {transactions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#666666', mb: 2 }}>
                  No transactions found
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {transactions.map((transaction) => (
                  <ListItem
                    key={transaction.id}
                    onClick={() => handleTransactionClick(transaction)}
                    sx={{
                      p: 2,
                      mb: 1,
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      minHeight: 'auto',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }
                    }}
                  >
                    {/* Left Icon */}
                    <Box sx={{
                      mr: 2,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'flex-start',
                      pt: 0.5
                    }}>
                      <Box sx={{
                        p: 1,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getTransactionIcon(transaction)}
                      </Box>
                    </Box>
                    
                    {/* Main Content */}
                    <Box sx={{ 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      minWidth: 0, // Allow text to wrap properly
                      pr: 2 // Add padding to separate from amount
                    }}>
                      {/* Transaction Type and Amount Row */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        mb: 1,
                        gap: 1
                      }}>
                        <Typography variant="subtitle1" sx={{ 
                          fontWeight: 600, 
                          flex: 1, 
                          mr: { xs: 1, sm: 2 },
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          lineHeight: 1.3
                        }}>
                          {transaction.type === 'BOOKING' ? 'Booking ID' : 
                           transaction.type === 'ORDER' ? 'Order ID' : 
                           transaction.type === 'refund' ? 'Refund' :
                           transaction.type === 'credit' ? 'Credit' :
                           transaction.type === 'debit' ? 'Debit' :
                           transaction.type === 'payment' ? 'Payment' :
                           'Transaction'} {transaction.referenceId ? `- ${transaction.referenceId}` : ''}
                        </Typography>
                        
                        {/* Amount - properly positioned */}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            color: getAmountColor(transaction.type),
                            flexShrink: 0,
                            textAlign: 'right',
                            minWidth: 'fit-content',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            lineHeight: 1.3
                          }}
                        >
                          {transaction.currency === 'GEMS' ? '' : '$'}
                          {transaction.currency === 'GEMS' ? Math.floor(transaction.amount) : transaction.amount.toFixed(2)}
                          {transaction.currency === 'GEMS' ? ' gems' : ''}
                        </Typography>
                      </Box>
                      
                      {/* Credit/Debit Badge */}
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={transaction.type}
                          size="small"
                          sx={{
                            backgroundColor: '#006AF9',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      </Box>
                      
                      {/* Description */}
                      <Typography variant="body2" sx={{ 
                        color: '#666666', 
                        mb: 1.5, 
                        lineHeight: 1.4,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      }}>
                        {transaction.description}
                      </Typography>
                      
                      {/* Date */}
                      <Typography variant="caption" sx={{ 
                        color: '#999999', 
                        mb: 1.5, 
                        display: 'block',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                      }}>
                        {formatDate(transaction.createdAt)}
                      </Typography>
                      
                      {/* Click to view details link */}
                      <Typography variant="caption" sx={{ 
                        display: 'block', 
                        color: '#006AF9', 
                        fontSize: { xs: '9px', sm: '10px' },
                        mt: 1,
                        mb: 0.5
                      }}>
                        Click to view details
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Filter Transactions</Typography>
            <Button
              startIcon={<Clear />}
              onClick={clearFilters}
              size="small"
            >
              Clear All
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Status Filter */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {transactionStatuses.map((status) => (
                <Chip
                  key={status}
                  label={status}
                  onClick={() => setSelectedStatus(status)}
                  color={selectedStatus === status ? 'primary' : 'default'}
                  variant={selectedStatus === status ? 'filled' : 'outlined'}
                />
              ))}
            </Box>

            {/* Transaction Type Filter */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Transaction Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {transactionFilters.map((filter) => (
                <Chip
                  key={filter}
                  label={filter}
                  onClick={() => setSelectedTransactionFilter(filter)}
                  color={selectedTransactionFilter === filter ? 'primary' : 'default'}
                  variant={selectedTransactionFilter === filter ? 'filled' : 'outlined'}
                />
              ))}
            </Box>

            {/* Date Range Filter */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Date Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={selectedDateRange?.start ? selectedDateRange.start.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : null;
                  if (date) {
                    setSelectedDateRange({
                      start: date,
                      end: selectedDateRange?.end || date
                    });
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                onClick={(e) => {
                  // Trigger the date picker when clicking anywhere in the field
                  e.target.showPicker && e.target.showPicker();
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input': {
                    cursor: 'pointer',
                  }
                }}
              />
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={selectedDateRange?.end ? selectedDateRange.end.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : null;
                  if (date) {
                    setSelectedDateRange({
                      start: selectedDateRange?.start || date,
                      end: date
                    });
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                onClick={(e) => {
                  // Trigger the date picker when clicking anywhere in the field
                  e.target.showPicker && e.target.showPicker();
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input': {
                    cursor: 'pointer',
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => setFilterDialogOpen(false)}
            variant="contained"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Wallet;
